/**
 * Dialog for å vise vedlegg (lydfiler) til en sang
 * Les-only dialog som fungerer for både admin og vanlige brukere
 */

import React from 'react';
import { FileAudio, Link as LinkIcon, ExternalLink, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MusikkFil } from '@/services/api';
import { musikkAPI } from '@/services/api/musikk.api';
import { aapneHttpAdresse } from '@/lib/aapneHttpAdresse';

interface AttachmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  musikkId: number;
  filer: MusikkFil[];
  sangTittel?: string;
}

export const AttachmentsDialog: React.FC<AttachmentsDialogProps> = ({
  open,
  onOpenChange,
  musikkId,
  filer,
  sangTittel,
}) => {
  const [loadingFileId, setLoadingFileId] = React.useState<number | null>(null);

  const handleOpenFile = async (e: React.MouseEvent, fil: MusikkFil) => {
    e.preventDefault();
    
    try {
      setLoadingFileId(fil.id);
      
      if (fil.fil_type === 'url' && fil.url) {
        // URL: åpnes bare hvis http(s) (eldre rader kan ha javascript:)
        aapneHttpAdresse(fil.url);
      } else if (fil.fil_type === 'upload') {
        // Upload: hent URL fra backend og åpne i ny tab
        const response = await musikkAPI.getFileUrl(musikkId, fil.id);
        aapneHttpAdresse(response.url);
      }
    } catch (error) {
      console.error('Kunne ikke åpne fil:', error);
    } finally {
      setLoadingFileId(null);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileAudio className="h-5 w-5 text-primary" />
            Vedlegg
            {sangTittel && (
              <span className="text-sm font-normal text-muted-foreground">
                - {sangTittel}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {filer.length === 1 
              ? '1 vedlegg registrert' 
              : `${filer.length} vedlegg registrert`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          {filer.length > 0 ? (
            <div className="border border-border rounded-lg divide-y divide-border bg-muted/20">
              {filer.map((fil) => (
                <button
                  key={fil.id}
                  type="button"
                  onClick={(e) => handleOpenFile(e, fil)}
                  disabled={loadingFileId === fil.id}
                  className="w-full p-3 flex items-center gap-3 hover:bg-muted/50 active:bg-muted/70 transition-colors cursor-pointer text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                  title={fil.fil_type === 'url' ? `Åpne ${fil.url}` : 'Åpne fil'}
                >
                  <div className="flex-shrink-0">
                    {fil.fil_type === 'upload' ? (
                      <FileAudio className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    ) : (
                      <LinkIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                      {fil.navn}
                    </p>
                    {fil.fil_type === 'upload' && fil.filstorrelse && (
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(fil.filstorrelse)}
                      </p>
                    )}
                    {fil.fil_type === 'url' && fil.url && (
                      <p className="text-xs text-muted-foreground truncate">
                        {fil.url}
                      </p>
                    )}
                  </div>
                  <Badge 
                    variant="outline" 
                    className="shrink-0 text-xs"
                  >
                    {fil.fil_type === 'upload' ? 'Fil' : 'URL'}
                  </Badge>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100 shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="p-6 bg-muted/20 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <FileAudio className="h-4 w-4" />
                Ingen vedlegg registrert
              </p>
            </div>
          )}
          
          {filer.length > 0 && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-2">
              <ExternalLink className="h-3 w-3" />
              Klikk på en fil for å åpne i nytt vindu
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

